import * as React from 'react';

/**
 * The canonical React 19 `useEffectEvent` type: `<T extends Function>(callback: T) => T`.
 *
 * We derive it from `React` itself (rather than re-declaring it) so this package
 * always stays in lockstep with the version of `@types/react` in the workspace
 * and never has to spell out the banned bare `Function` type in our own source.
 */
type UseEffectEvent = typeof React.useEffectEvent;

/**
 * React 16 implementation of `useEffectEvent`.
 *
 * Returns a stable function identity whose call always runs the latest handler,
 * mirroring the semantics of React 19's `useEffectEvent`. The handler ref is
 * refreshed synchronously after every render (via `useLayoutEffect`) but before
 * the browser paints, so the returned callback never sees a stale closure.
 */
const useEffectEventPolyfill: UseEffectEvent = (handler) => {
  const handlerRef = React.useRef(handler);

  // Update the ref synchronously after every render but before the browser
  // paints. This guarantees the ref always holds the freshest closure scope.
  React.useLayoutEffect(() => {
    handlerRef.current = handler;
  });

  // Return a stable function identity that never changes.
  const stableFn = React.useCallback(
    (...args: unknown[]) =>
      ((handlerRef.current as unknown) as (...innerArgs: unknown[]) => unknown)(
        ...args
      ),
    []
  );

  return (stableFn as unknown) as typeof handler;
};

// React 19.2 ships `useEffectEvent` natively; earlier majors (e.g. the React 16
// apps that consume this workspace) do not. Pick the native hook when the
// running React is >= 19 and actually exposes it, otherwise fall back to the
// React 16 polyfill above. The choice is made once at module load, so whichever
// implementation is selected still calls its hooks unconditionally every render.
const reactMajorVersion = Number.parseInt(React.version, 10);

/**
 * A drop-in replacement for React 19's `useEffectEvent` that also works on the
 * React 16 apps consuming this workspace. Same signature and semantics as the
 * canonical hook.
 *
 * @see {@link https://react.dev/reference/react/useEffectEvent}
 */
export const useEffectEvent: UseEffectEvent =
  reactMajorVersion >= 19 && typeof React.useEffectEvent === 'function'
    ? React.useEffectEvent
    : useEffectEventPolyfill;
